import React from 'react';

/**
 * Commerce Index Logo - Complete Brand System
 * 
 * Dual-palette system:
 * - Dark theme: Aqua (#3EF1C7) with glow
 * - Light theme: Deep teal (#007F6D) no glow
 * 
 * Variants:
 * - primary: Full ring + CI monogram
 * - monogram: CI only (for favicons)
 * - ring: Ring only (for badges)
 */

export const CILogo = ({ 
  size = 'md',  // sm: 32px, md: 40px, lg: 48px, xl: 56px, custom: number
  variant = 'primary',  // primary | monogram | ring
  theme = 'dark',  // dark | light | auto
  showText = false,
  className = ''
}) => {
  // Size mapping
  const sizeMap = {
    sm: 32,
    md: 40,
    lg: 48,
    xl: 56
  };
  
  const logoSize = typeof size === 'number' ? size : sizeMap[size];
  
  // Auto-detect theme based on background if 'auto'
  const effectiveTheme = theme === 'auto' 
    ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;
  
  // Color palette by theme
  const colors = effectiveTheme === 'dark' ? {
    ring: '#3EF1C7',
    ticks: '#3EF1C7',
    monogram: '#C7FFF0',
    glow: 'rgba(62, 241, 199, 0.35)',  // Enhanced glow
    glowBlur: 20,
    hasGlow: true
  } : {
    ring: '#007F6D',
    ticks: '#009E82',
    monogram: '#004C44',
    glow: 'rgba(0, 127, 109, 0.25)',  // Teal glow for light theme
    glowBlur: 16,
    hasGlow: true  // Enable glow on light theme too
  };
  
  // Primary Logo: Full Intelligence Ring + CI Monogram
  const PrimaryLogo = () => (
    <svg 
      width={logoSize} 
      height={logoSize} 
      viewBox="0 0 44 44" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Commerce Index"
      style={{ filter: colors.hasGlow ? `drop-shadow(0 0 ${colors.glowBlur}px ${colors.glow})` : 'none' }}
    >
      {/* Subtle inner glow */}
      {colors.hasGlow && (
        <>
          <circle cx="22" cy="22" r="22" fill={colors.glow} opacity="0.15" />
          <circle cx="22" cy="22" r="20" fill={colors.glow} opacity="0.2" />
        </>
      )}
      
      {/* Main ring */}
      <circle 
        cx="22" 
        cy="22" 
        r="20" 
        fill="none" 
        stroke={colors.ring} 
        strokeWidth={effectiveTheme === 'light' ? "3.5" : "3"}
      />
      
      {/* 12 Intelligence Ticks */}
      <g fill={colors.ticks}>
        {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
          <rect 
            key={i}
            x="21" 
            y="4" 
            width="2" 
            height="6" 
            rx="1" 
            transform={`rotate(${angle} 22 22)`}
          />
        ))}
      </g>
      
      {/* CI Monogram */}
      <text 
        x="22" 
        y="27"
        textAnchor="middle"
        fontFamily="'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        fontSize="14"
        fontWeight="700"
        fill={colors.monogram}
        style={{ userSelect: 'none' }}
      >
        CI
      </text>
    </svg>
  );
  
  // Monogram-Only Variant (for favicons, small contexts)
  const MonogramLogo = () => (
    <svg 
      width={logoSize} 
      height={logoSize} 
      viewBox="0 0 32 32" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="CI"
    >
      {/* Background circle */}
      <circle 
        cx="16" 
        cy="16" 
        r="16" 
        fill={effectiveTheme === 'dark' ? '#0B1120' : '#F2FBFA'}
      />
      
      {/* CI monogram */}
      <text 
        x="16" 
        y="21"
        textAnchor="middle"
        fontFamily="'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        fontSize="13"
        fontWeight="700"
        fill={colors.monogram}
        style={{ userSelect: 'none' }}
      >
        CI
      </text>
    </svg>
  );
  
  // Ring-Only Variant (for badges, loading states)
  const RingLogo = () => (
    <svg 
      width={logoSize} 
      height={logoSize} 
      viewBox="0 0 44 44" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="CI Score"
      style={{ filter: colors.hasGlow ? `drop-shadow(0 0 ${colors.glowBlur}px ${colors.glow})` : 'none' }}
    >
      {/* Glow layers */}
      {colors.hasGlow && (
        <>
          <circle cx="22" cy="22" r="22" fill={colors.glow} opacity="0.12" />
          <circle cx="22" cy="22" r="20" fill={colors.glow} opacity="0.18" />
        </>
      )}
      
      {/* Ring */}
      <circle 
        cx="22" 
        cy="22" 
        r="20" 
        fill="none" 
        stroke={colors.ring} 
        strokeWidth="3"
      />
      
      {/* 12 Ticks */}
      <g fill={colors.ticks}>
        {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
          <rect 
            key={i}
            x="21" 
            y="4" 
            width="2" 
            height="6" 
            rx="1" 
            transform={`rotate(${angle} 22 22)`}
          />
        ))}
      </g>
    </svg>
  );
  
  // Logo with text lockup
  if (showText) {
    return (
      <div className={`inline-flex items-center gap-3 ${className}`}>
        <PrimaryLogo />
        <div className={`font-bold text-xl ${effectiveTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Commerce Index
        </div>
      </div>
    );
  }
  
  // Render selected variant
  switch (variant) {
    case 'monogram':
      return <MonogramLogo />;
    case 'ring':
      return <RingLogo />;
    case 'primary':
    default:
      return <PrimaryLogo />;
  }
};

// Export preset configurations
export const CILogoNav = ({ theme = 'auto' }) => (
  <CILogo size={40} variant="primary" theme={theme} showText={true} />
);

export const CILogoIcon = ({ size = 24, variant = 'ring', theme = 'dark' }) => (
  <CILogo size={size} variant={variant} theme={theme} />
);

export const CILogoFavicon = () => (
  <CILogo size={32} variant="monogram" theme="dark" />
);

export const CILogoBadge = ({ theme = 'dark' }) => (
  <CILogo size={24} variant="ring" theme={theme} />
);

export const CILogoHero = () => (
  <CILogo size={56} variant="primary" theme="dark" />
);

// Export default
export default CILogo;
